/**
 * Command Palette Component
 * 
 * Global search dialog that opens with Ctrl-O (or Cmd-O on Mac)
 * Searches across all content types and opens items in context-aware manner
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/InputDefault';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';
import {
  searchAll,
  getResultCount,
  flattenResults,
  SearchResult,
  GroupedSearchResults,
} from '@/lib/services/CommandPaletteSearchService';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useTabContext } from '@/contexts/TabContext';
import { cn } from '@/lib/utils';
import {
  Library,
  FileText,
  MessageSquare,
  Blocks,
  Folder,
  Gamepad2,
  Search,
} from 'lucide-react';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const iconMap = {
  Library,
  FileText,
  MessageSquare,
  Blocks,
  Folder,
  Gamepad2,
};

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  
  const { selectedGame, games, selectGame } = useSimplifiedUserData();
  const { openTab } = useTabContext();
  const navigate = useNavigate();
  
  // Debounced search results
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 300);
    
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);
  
  // Perform search
  const results: GroupedSearchResults = useMemo(() => {
    return searchAll(debouncedSearchTerm, selectedGame?.id, games);
  }, [debouncedSearchTerm, selectedGame?.id, games]);
  
  const flatResults = useMemo(() => flattenResults(results), [results]);
  const resultCount = useMemo(() => getResultCount(results), [results]);
  
  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [resultCount]);
  
  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSelectedIndex(0);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);
  
  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % flatResults.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + flatResults.length) % flatResults.length);
      } else if (e.key === 'Enter' && flatResults.length > 0) {
        e.preventDefault();
        handleSelectResult(flatResults[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [flatResults, selectedIndex, onClose]
  );
  
  // Scroll selected item into view
  useEffect(() => {
    if (resultsContainerRef.current) {
      const selectedElement = resultsContainerRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [selectedIndex]);
  
  // Handle result selection
  const handleSelectResult = useCallback(
    (result: SearchResult) => {
      onClose();
      
      switch (result.type) {
        case 'game':
          // Switch to the selected game
          if (result.gameId) {
            selectGame(result.gameId);
            navigate('/home');
          }
          break;
          
        case 'document':
          // Open document in a new tab (path is used as unique ID)
          openTab({
            id: result.path,
            title: result.title,
            path: result.path,
            icon: FileText,
          });
          navigate(result.path);
          break;
          
        case 'glossary':
          // Navigate to library with item selected
          navigate(result.path);
          break;
          
        case 'dialog':
          // Open dialog in a new tab (path is used as unique ID)
          openTab({
            id: result.path,
            title: result.title,
            path: result.path,
            icon: MessageSquare,
          });
          navigate(result.path);
          break;
          
        case 'ui-element':
          // Navigate to UI elements with item selected
          navigate(result.path);
          break;
          
        case 'folder':
          // Navigate to the appropriate page with folder selected
          navigate(result.path);
          break;
      }
    },
    [onClose, selectGame, openTab, navigate]
  );
  
  // Render icon
  const renderIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName as keyof typeof iconMap] || FileText;
    return <IconComponent className="w-4 h-4" />;
  };
  
  // Render result item
  const renderResultItem = (result: SearchResult, index: number) => {
    const isSelected = index === selectedIndex;
    
    return (
      <div
        key={`${result.type}-${result.id}`}
        data-index={index}
        className={cn(
          'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors',
          isSelected
            ? 'bg-accent text-accent-foreground'
            : 'hover:bg-accent/50'
        )}
        onClick={() => handleSelectResult(result)}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <div className="flex-shrink-0 text-muted-foreground">
          {renderIcon(result.icon)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-center">{result.title}</div>
          {result.subtitle && (
            <div className="text-xs text-muted-foreground truncate text-center">
              {result.subtitle}
            </div>
          )}
        </div>
        {result.tags && result.tags.length > 0 && (
          <div className="flex gap-1 flex-shrink-0">
            {result.tags.slice(0, 2).map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs px-1 py-0">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // Render results section
  const renderResultsSection = (title: string, results: SearchResult[], startIndex: number) => {
    if (results.length === 0) return null;
    
    return (
      <div className="mb-2">
        <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center">
          {title}
        </div>
        <div>
          {results.map((result, idx) => renderResultItem(result, startIndex + idx))}
        </div>
      </div>
    );
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 max-h-[80vh] flex flex-col">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-center">Quick Search</DialogTitle>
        </DialogHeader>
        
        {/* Search Input */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search documents, glossary items, dialogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-10 text-center"
            />
          </div>
        </div>
        
        {/* Results */}
        <div
          ref={resultsContainerRef}
          className="flex-1 overflow-y-auto border-t"
        >
          {isSearching ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="sm" text="Searching..." />
            </div>
          ) : resultCount === 0 && debouncedSearchTerm ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <Search className="w-12 h-12 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                No results found for "{debouncedSearchTerm}"
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Try searching for documents, glossary items, dialogs, or folders
              </p>
            </div>
          ) : resultCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <Search className="w-12 h-12 text-muted-foreground mb-3" />
              <p className="text-sm font-medium">Start typing to search</p>
              <p className="text-xs text-muted-foreground mt-2">
                Search across games, documents, glossary items, dialogs, and more
              </p>
            </div>
          ) : (
            <>
              {renderResultsSection('Games', results.games, 0)}
              {renderResultsSection(
                'Documents',
                results.documents,
                results.games.length
              )}
              {renderResultsSection(
                'Glossary Items',
                results.glossary,
                results.games.length + results.documents.length
              )}
              {renderResultsSection(
                'Dialogs',
                results.dialogs,
                results.games.length + results.documents.length + results.glossary.length
              )}
              {renderResultsSection(
                'UI Elements',
                results.uiElements,
                results.games.length +
                  results.documents.length +
                  results.glossary.length +
                  results.dialogs.length
              )}
              {renderResultsSection(
                'Folders',
                results.folders,
                results.games.length +
                  results.documents.length +
                  results.glossary.length +
                  results.dialogs.length +
                  results.uiElements.length
              )}
            </>
          )}
        </div>
        
        {/* Footer with keyboard shortcuts */}
        {resultCount > 0 && (
          <div className="border-t px-4 py-2 text-xs text-muted-foreground text-center">
            <span className="font-mono">↑↓</span> Navigate •{' '}
            <span className="font-mono">↵</span> Open •{' '}
            <span className="font-mono">ESC</span> Close
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;

